import React from 'react';
import TokenAutosuggestField from '../Common/TokenAutosuggestField';
import WebhookTokenReference from './WebhookTokenReference';
import WebhookHeadersEditor from './WebhookHeadersEditor';
import {
    headersForTest,
    IHeaderRow,
    toHeaderPayload,
    toHeaderRows,
    validateHeaderRows
} from './webhookHeaders';
import { toast } from 'react-toastify';
import { bodyIgnoresTokens, useWebhookTokens } from './useWebhookTokens';
import {
    IWebhook,
    WEBHOOK_TYPE_SF_CART_COMMENT,
    WEBHOOK_TYPE_SF_ORDER,
    WEBHOOK_TYPE_SF_QUOTE_REQUEST,
    WEBHOOK_TYPE_SF_SIGNUP,
    WEBHOOK_TYPE_SF_SIGNUP_APPROVAL,
    PROCUREMENT_WEBHOOK_TYPES,
    WEBHOOK_TYPE_PROCUREMENT_DEMAND,
    WEBHOOK_TYPE_PROCUREMENT_ORDER,
    WEBHOOK_TYPE_PROCUREMENT_ORDER_SHIPMENT_UPDATE,
    WEBHOOK_TYPE_PROCUREMENT_ORDER_UPDATE, WEBHOOK_TYPE_DPL_EXPORT,
    WEBHOOK_TYPE_EXTENSION_ERROR
} from '../../models/Webhooks';
import {
    DefaultButton,
    Dropdown,
    IconButton,
    IDropdownOption,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    Pivot,
    PivotItem,
    Spinner,
    SpinnerSize,
    Stack,
    TextField
} from '@fluentui/react';
import ToggleField from '../Common/ToggleField';
import WebhookIdsDropdown from './WebhookIdsDropdown';
import WebhookLogs from './WebhookLogs';
import {useCreateWebhookMutation, usePatchWebhookMutation} from '../../store/endpoints/WebhooksEndpoints';
import DataField from "../Common/DataField";
import VendorSelectionDropdown from "../Vendors/VendorSelectionDropdown";
import ConditionalSetting from "../Common/ConditionalSetting";

interface IWebhookPanelProps {
    webhook?: IWebhook;
    isOpen: boolean;
    onDismiss: () => void;
    /** The tab to open on: 'logs' from a link about the webhook's failing calls, otherwise Settings. */
    initialTab?: string;
}

const WEBHOOK_TYPES: IDropdownOption[] = [
    { key: WEBHOOK_TYPE_SF_ORDER, text: 'Storefront Order' },
    { key: WEBHOOK_TYPE_SF_QUOTE_REQUEST, text: 'Quote Request' },
    { key: WEBHOOK_TYPE_SF_CART_COMMENT, text: 'Cart Customer Comment' },
    { key: WEBHOOK_TYPE_SF_SIGNUP, text: 'Storefront Signup' },
    { key: WEBHOOK_TYPE_SF_SIGNUP_APPROVAL, text: 'Storefront Signup Awaiting Approval' },
    { key: WEBHOOK_TYPE_PROCUREMENT_ORDER, text: 'Procurement Order Created' },
    { key: WEBHOOK_TYPE_PROCUREMENT_DEMAND, text: 'Procurement Demand Created' },
    { key: WEBHOOK_TYPE_PROCUREMENT_ORDER_UPDATE, text: 'Procurement Order Updated' },
    { key: WEBHOOK_TYPE_PROCUREMENT_ORDER_SHIPMENT_UPDATE, text: 'Procurement Order Shipment Updated' },
    { key: WEBHOOK_TYPE_DPL_EXPORT, text: 'Export Dynamic Product Listing' },
    { key: WEBHOOK_TYPE_EXTENSION_ERROR, text: 'Extension Error' },
];

const labelForType = (type?: string): string =>
    (WEBHOOK_TYPES.find((t) => t.key === type)?.text) ?? '';

const REQUEST_BODY_TOOLTIP = 'The JSON that will be sent in the body of the webhook request. You can use handlebars syntax to include dynamic data from the event that triggers the webhook. If not included, a default contextual entity will be sent as the payload.';

const WebhookPanel: React.FC<IWebhookPanelProps> = (props) => {
    const [webhook, setWebhook] = React.useState<IWebhook>({});
    const [createWebhook] = useCreateWebhookMutation();
    const [patchWebhook] = usePatchWebhookMutation();
    const [testStatus, setTestStatus] = React.useState<{ type: MessageBarType; message: string } | null>(null);
    const [isTesting, setIsTesting] = React.useState(false);
    // Header rows carry local-only fields (row key, name as loaded), so they live beside the webhook
    // rather than inside it and are converted to the API shape on save.
    const [headerRows, setHeaderRows] = React.useState<IHeaderRow[]>([]);
    const [isSaving, setIsSaving] = React.useState(false);
    const [activeTab, setActiveTab] = React.useState('settings');
    // Whether this webhook sends a body of its own. Derived state, not stored: an empty requestBody IS
    // "send the default payload" as far as the dispatcher is concerned, so the toggle reflects that
    // rather than adding a second, desynchronizable source of truth.
    const [useCustomPayload, setUseCustomPayload] = React.useState(false);
    // Holds the body while the toggle is off, so flicking it off and back on in one sitting doesn't
    // cost the admin their JSON. Deliberately not persisted -- once saved, off means no body.
    const stashedBody = React.useRef<string | undefined>();

    const testWebhook = async () => {
        if (!webhook.url) return;
        setTestStatus(null);
        setIsTesting(true);
        let requestBody = '{}';
        if (webhook.requestBody) {
            try {
                requestBody = JSON.stringify(JSON.parse(webhook.requestBody));
            } catch {
                setTestStatus({ type: MessageBarType.error, message: 'Request body must be valid JSON.' });
                setIsTesting(false);
                return;
            }
        }
        try {
            // Custom headers go out as the user typed them (tokens are not rendered, same as the body).
            // The default content type is applied first so a custom Content-Type header can override it.
            const { headers: customHeaders, omittedSavedSecret } = headersForTest(headerRows);
            const requestHeaders = new Headers({ 'Content-Type': 'application/json' });
            Object.keys(customHeaders).forEach((name) => requestHeaders.set(name, customHeaders[name]));
            const note = omittedSavedSecret ? ' Saved secret headers are not included in test requests.' : '';
            const response = await fetch(webhook.url, {
                method: 'POST',
                headers: requestHeaders,
                body: requestBody|| '{}',
            });
            if (response.ok) {
                setTestStatus({ type: MessageBarType.success, message: `Success: received ${response.status} ${response.statusText}.${note}` });
            } else {
                setTestStatus({ type: MessageBarType.error, message: `Failed: received ${response.status} ${response.statusText}.${note}` });
            }
        } catch (e: any) {
            setTestStatus({ type: MessageBarType.error, message: `Error sending request: ${e?.message ?? e}` });
        } finally {
            setIsTesting(false);
        }
    };

    React.useEffect(() => {
        setWebhook(props.webhook ?? {});
        setHeaderRows(toHeaderRows(props.webhook?.headers));
        setActiveTab(props.initialTab === 'logs' ? 'logs' : 'settings');
        setUseCustomPayload(!!props.webhook?.requestBody);
        stashedBody.current = undefined;
    }, [props.webhook, props.isOpen]);

    // Off clears the body, because that is what the server reads to mean "send the default payload" --
    // leaving it set would keep sending it while the UI claimed otherwise.
    const onUseCustomPayloadChange = (checked: boolean) => {
        setUseCustomPayload(checked);
        setTestStatus(null);
        if (checked) {
            setWebhook({ ...webhook, requestBody: stashedBody.current ?? webhook.requestBody ?? '' });
        } else {
            stashedBody.current = webhook.requestBody;
            setWebhook({ ...webhook, requestBody: '' });
        }
    };

    const tokens = useWebhookTokens(webhook.type);
    const requestBodyTestAction = webhook.requestBody && webhook.url
        ? isTesting
            ? <Spinner size={SpinnerSize.xSmall} />
            : <IconButton iconProps={{iconName: 'TestBeaker'}} title="Send test request" onClick={testWebhook} />
        : undefined;

    // Picking a type names the webhook after it. Most people never care what it is called -- the type
    // is the useful label -- but the field stays editable, so the prefill must not clobber a name
    // somebody typed. It only writes when the name is empty or is still the previous type's prefill,
    // which is exactly the case where the name carries no decision of its own.
    const onTypeChange = (type?: string) => {
        const isPrefilled = !webhook.name || webhook.name === labelForType(webhook.type);
        setWebhook({ ...webhook, type, name: isPrefilled ? labelForType(type) : webhook.name });
    };

    const isProcurementType = PROCUREMENT_WEBHOOK_TYPES.includes(webhook.type ?? '');

    // A rejected save can surface two ways: a resolved {success: false}, or a rejected mutation
    // (non-2xx) whose body carries the same IActionResponse. Either way the server message is the
    // useful part.
    const saveErrorMessage = (error: any): string =>
        error?.data?.message ?? error?.message ?? 'The webhook could not be saved.';

    const save = async () => {
        if (isSaving) return;
        // Always send the full header list: omitting it would leave stored headers untouched, so an
        // empty array is how removing the last one reaches the server.
        const data: IWebhook = { ...webhook, headers: toHeaderPayload(headerRows) };
        setIsSaving(true);
        const toastId = toast.loading('Saving webhook...');
        try {
            const result = webhook.id
                ? await patchWebhook({data, id: webhook.id}).unwrap()
                : await createWebhook(data).unwrap();
            if (result?.success === false) throw new Error(result.message || 'The webhook could not be saved.');
            toast.update(toastId, { type: 'success', render: 'Webhook saved', isLoading: false, autoClose: 3000 });
            props.onDismiss();
        } catch (error) {
            // Keep the panel open so the user can fix the problem without retyping.
            toast.update(toastId, { type: 'error', render: saveErrorMessage(error), isLoading: false, autoClose: 8000 });
        } finally {
            setIsSaving(false);
        }
    }

    const isValid = (): boolean => {
        return !!webhook.name && webhook.name.length > 0
            && !!webhook.url && webhook.url.length > 0
            && !!webhook.type && webhook.type.length > 0
            && validateHeaderRows(headerRows).every((e) => !e);
    }

    const settingsContent = (
                    <Stack tokens={{childrenGap: '1em'}} style={{marginTop: '1em'}}>
                        {/* Type leads, and nothing else appears until it is set. It decides what the
                            rest of the form even means -- which tokens the body can use, whether the
                            scoping dropdown lists storefronts or extensions, whether that dropdown
                            appears at all -- so asking for it first is the difference between filling
                            the form in once and filling it in and then revising it. */}
                        <Dropdown
                            data-setting-id='webhooks.details.type'
                            label='Webhook Type'
                            options={WEBHOOK_TYPES}
                            selectedKey={webhook.type ?? null}
                            placeholder='Select what this webhook fires on'
                            // Fixed once saved. The type is not a label -- the scoping ids underneath
                            // it are storefront ids for one type and extension ids for another, and a
                            // custom body is written against that event's tokens. Switching it on a
                            // live webhook leaves both silently pointing at nothing, with no error and
                            // no sign in the UI. A different event is a different webhook.
                            disabled={!!webhook.id}
                            onChange={(_, option) => onTypeChange(option?.key as string)}
                        />
                        {!!webhook.id && (
                            <p style={{margin: 0, fontSize: 12, color: '#605e5c'}}>
                                The type is set when the webhook is created. To fire on a different
                                event, add a new webhook.
                            </p>
                        )}
                        {!webhook.type && (
                            <p style={{margin: 0, fontSize: 12, color: '#605e5c'}}>
                                Pick a type to set up the rest of the webhook.
                            </p>
                        )}
                        {!!webhook.type && <>
                        <TextField
                            data-setting-id='webhooks.details.name'
                            label='Name'
                    value={webhook.name || ''}
                    onChange={(_, newValue) => setWebhook({...webhook, name: newValue || ''})}
                    />
                <TextField
                    data-setting-id='webhooks.details.url'
                    label='URL'
                    value={webhook.url || ''}
                    onChange={(_, newValue) => setWebhook({...webhook, url: newValue || ''})}
                />
                <WebhookHeadersEditor type={webhook.type} rows={headerRows} onChange={setHeaderRows} />
                {/* Stated in a caption rather than ToggleField's toolTip: that prop renders its info
                    icon as a second row of the toggle's Stack unless `inline` is set, which would put
                    an orphaned icon under the switch. */}
                <div>
                    <ToggleField
                        settingId='webhooks.details.useCustomPayload'
                        label='Use Custom Payload'
                        checked={useCustomPayload}
                        onChange={(_, checked) => onUseCustomPayloadChange(!!checked)}
                    />
                    <div style={{fontSize: 12, color: '#605e5c'}}>
                        Off sends the default payload for this event.
                    </div>
                </div>
                {/* A body that is never rendered is worse than no body: it looks configured and is
                    silently dropped in favour of the default payload. Say so as soon as the toggle
                    goes on, rather than after the JSON has been written. */}
                {useCustomPayload && bodyIgnoresTokens(webhook.type) && (
                    <MessageBar messageBarType={MessageBarType.info} isMultiline>
                        This webhook type sends its own payload and does not substitute tokens, so a
                        custom request body is ignored.
                    </MessageBar>
                )}
                {/* The body field offers this event's tokens as you type an opening brace, and the tag
                    button lists them all. Both are driven by the selected type, because each event
                    renders its own token set — see useWebhookTokens. Types with no token pass keep the
                    plain field rather than a suggest box that would offer tokens that never render. */}
                <ConditionalSetting when={useCustomPayload} id='webhooks.details.requestBody'>
                    {tokens.supported ? (
                        <TokenAutosuggestField
                            settingId='webhooks.details.requestBody'
                            toolTip={REQUEST_BODY_TOOLTIP}
                            label='Request JSON'
                            labelActions={<>
                                <WebhookTokenReference type={webhook.type} />
                                {requestBodyTestAction}
                            </>}
                            multiline
                            rows={6}
                            suggestions={tokens.suggestions}
                            value={webhook.requestBody || ''}
                            onChange={(newValue) => { setWebhook({...webhook, requestBody: newValue || ''}); setTestStatus(null); }}
                        />
                    ) : (
                        <DataField
                            settingId='webhooks.details.requestBody'
                            toolTip={REQUEST_BODY_TOOLTIP}
                            label='Request JSON'
                            labelActions={requestBodyTestAction}
                            multiline={true}
                            value={webhook.requestBody || ''}
                            onChange={(_, newValue) => { setWebhook({...webhook, requestBody: newValue || ''}); setTestStatus(null); }}
                        />
                    )}
                </ConditionalSetting>
                {testStatus && (
                    <MessageBar messageBarType={testStatus.type} isMultiline={false} onDismiss={() => setTestStatus(null)}>
                        {testStatus.message}
                    </MessageBar>
                )}
                <ConditionalSetting when={webhook.type !== WEBHOOK_TYPE_DPL_EXPORT} id='webhooks.details.globalEnabled'>
                    <ToggleField
                        settingId='webhooks.details.globalEnabled'
                        label='Enabled Globally'
                        checked={webhook.globalEnabled ?? false}
                        onChange={(_, checked) => setWebhook({...webhook, globalEnabled: !!checked})}
                    />
                </ConditionalSetting>
                {!isProcurementType && webhook.type !== WEBHOOK_TYPE_DPL_EXPORT &&  (
                    <WebhookIdsDropdown
                        type={webhook.type || ''}
                        enabledIds={webhook.enabledIds}
                        onChange={(enabledIds) => setWebhook({...webhook, enabledIds})}
                        disabled={!webhook.type || webhook.globalEnabled}
                    />
                )}
                <ConditionalSetting when={webhook.type === WEBHOOK_TYPE_DPL_EXPORT} id='webhooks.details.vendor'>
                    <VendorSelectionDropdown
                        settingId='webhooks.details.vendor'
                        onChange={(vendorId) => setWebhook({...webhook, vendor: vendorId})}
                        selectedVendor={webhook.vendor}
                    />
                </ConditionalSetting>
                        </>}
                    </Stack>
    );

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            headerText={props.webhook?.name || 'New Webhook'}
            type={PanelType.medium}
            closeButtonAriaLabel="Close"
            isFooterAtBottom={true}
            onRenderFooterContent={() => activeTab === 'logs' ? null : (<Stack horizontal tokens={{childrenGap: '1em'}} grow>
                <DefaultButton disabled={!isValid() || isSaving} style={{width: '100%'}} primary onClick={save}>Save</DefaultButton>
                <DefaultButton style={{width: '100%'}} onClick={props.onDismiss}>Cancel</DefaultButton>
            </Stack>)}
        >
            {/* The tab strip only earns its place once there is a second tab. A brand new webhook has
                no logs to show, so it gets the form directly rather than a lone "Settings" tab. */}
            {props.webhook ? (
                <Pivot selectedKey={activeTab} onLinkClick={(item) => setActiveTab(item?.props.itemKey ?? 'settings')}>
                    <PivotItem itemKey='settings' headerText='Settings' itemIcon='Settings'>
                        {settingsContent}
                    </PivotItem>
                    <PivotItem itemKey='logs' headerText='Logs' itemIcon='History'>
                        <div style={{marginTop: '1em'}}>
                            <WebhookLogs webhook={props.webhook} />
                        </div>
                    </PivotItem>
                </Pivot>
            ) : settingsContent}
        </Panel>
    )
}

export default WebhookPanel;